import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { regioniProvince } from "@/data/regioniProvince";
import { CheckboxLabel } from "@/components/CheckboxLabel";
import { trackLeadConversion } from "@/components/TrackingScripts";
import { trackLandingConversion } from "@/lib/landingTracking";
import {
  ArrowRight, ArrowLeft, Home, Building2, Warehouse, Castle,
  Sun, Zap, Loader2, CheckCircle2, Phone,
} from "lucide-react";

const API = "/api";

// Same values as SavingsCalculator.jsx
const IRRADIANCE = {
  "Abruzzo": 1350, "Basilicata": 1500, "Calabria": 1550, "Campania": 1450,
  "Emilia-Romagna": 1250, "Friuli Venezia Giulia": 1200, "Lazio": 1400,
  "Liguria": 1300, "Lombardia": 1200, "Marche": 1300, "Molise": 1400,
  "Piemonte": 1200, "Puglia": 1550, "Sardegna": 1600, "Sicilia": 1650,
  "Toscana": 1350, "Trentino-Alto Adige": 1150, "Umbria": 1350,
  "Valle d'Aosta": 1150, "Veneto": 1220,
};

const PROPERTY_TYPES = [
  { id: "appartamento", label: "Appartamento", icon: Building2 },
  { id: "villetta", label: "Villetta / Casa", icon: Home },
  { id: "bifamiliare", label: "Bifamiliare", icon: Warehouse },
  { id: "villa", label: "Villa", icon: Castle },
];

const BILL_RANGES = [
  { id: "50", label: "Fino a 50€/mese", value: 50 },
  { id: "80", label: "50€ - 80€/mese", value: 80 },
  { id: "120", label: "80€ - 120€/mese", value: 120 },
  { id: "180", label: "120€ - 180€/mese", value: 180 },
  { id: "250", label: "180€ - 250€/mese", value: 250 },
  { id: "350", label: "Oltre 250€/mese", value: 350 },
];

function validateItalianPhone(p) {
  const d = p.replace(/[\s\-\+]/g, "");
  return /^(39)?3\d{8,9}$/.test(d) || /^3\d{8,9}$/.test(d);
}

export function CalculatorLeadForm({ landing, theme, otpRequired }) {
  const [step, setStep] = useState(0); // 0=property, 1=bill, 2=region, 3=result, 4=contact, 5=otp, 6=done
  const [calc, setCalc] = useState({ property_type: "", monthly_bill: "", bill_value: 0, region: "" });
  const [savings, setSavings] = useState(0);
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", province: "", privacy_accepted: false });
  const [privacyText, setPrivacyText] = useState("");
  const [privacyLink, setPrivacyLink] = useState({ text: "", url: "" });
  const [extraCheckboxes, setExtraCheckboxes] = useState([]);
  const [extraChecked, setExtraChecked] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    axios.get(`${API}/privacy-text`).then((r) => {
      setPrivacyText(r.data.text || "");
      setPrivacyLink({ text: r.data.link_text || "", url: r.data.link_url || "" });
      setExtraCheckboxes(r.data.checkboxes || []);
    }).catch(() => {});
  }, []);

  const provinces = calc.region ? (regioniProvince[calc.region] || []) : [];

  const calculateSavings = (region, billValue) => {
    const irr = IRRADIANCE[region] || 1350;
    const electricityPrice = 0.25;
    const selfWithBatt = 0.90;

    const annualBill = billValue * 12;
    const annualConsumption = annualBill / electricityPrice;
    const systemSize = Math.ceil(annualConsumption / irr * 10) / 10;
    const annualProduction = systemSize * irr;
    const savingsWithBatt = Math.round(annualProduction * selfWithBatt * electricityPrice);

    return savingsWithBatt;
  };

  const goToResult = (region) => {
    const s = calculateSavings(region, calc.bill_value);
    setSavings(s);
    setStep(3);
  };

  const handlePhoneChange = (val) => {
    setForm(p => ({ ...p, phone: val }));
    setPhoneError(val && !validateItalianPhone(val) ? "Numero italiano non valido" : "");
  };

  const sendOtp = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/otp/send`, { phone: form.phone });
      setOtpSent(true);
      toast.success("Codice OTP inviato!");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nell'invio OTP"); }
    finally { setVerifying(false); }
  };

  const verifyOtp = async () => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/otp/verify`, { phone: form.phone, code: otpCode });
      if (res.data.success) { setPhoneVerified(true); toast.success("Numero verificato!"); }
    } catch (err) { toast.error(err.response?.data?.detail || "Codice non valido o scaduto"); }
    finally { setVerifying(false); }
  };

  const submit = async () => {
    if (!form.name || !form.surname || !form.email || !form.phone) { toast.error("Compila tutti i campi obbligatori"); return; }
    if (!form.province) { toast.error("Seleziona la provincia"); return; }
    if (!form.privacy_accepted) { toast.error("Devi accettare la privacy"); return; }
    if (extraCheckboxes.some((cb, i) => cb.required && !extraChecked[i])) { toast.error("Accetta tutti i consensi obbligatori"); return; }
    if (otpRequired && !phoneVerified) { toast.error("Verifica il numero di telefono"); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/landings/lead`, {
        name: form.name, surname: form.surname, email: form.email, phone: form.phone,
        province: form.province, privacy_accepted: form.privacy_accepted,
        landing_slug: landing.slug, product_name: landing.product_name,
        property_type: calc.property_type, monthly_bill: calc.monthly_bill,
        region: calc.region, estimated_savings: `${savings}`,
      });
      trackLeadConversion({ source: "landing_promo", name: form.name, email: form.email, landing_slug: landing.slug, product_name: landing.product_name });
      trackLandingConversion(landing.tracking_codes, { landing_slug: landing.slug, product_name: landing.product_name });
      setStep(6);
    } catch { toast.error("Errore invio richiesta"); }
    finally { setLoading(false); }
  };

  const canSubmitContact = form.name && form.surname && form.email && form.phone && form.province && form.privacy_accepted
    && !phoneError && extraCheckboxes.every((cb, i) => !cb.required || extraChecked[i])
    && (!otpRequired || phoneVerified);

  // Done
  if (step === 6) {
    return (
      <div className="text-center py-8 space-y-3" data-testid="calculator-form-done">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
        <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Richiesta Inviata!</h3>
        <p className="text-sm text-slate-500">Ti contatteremo al piu presto con un preventivo personalizzato.</p>
        <div className={`inline-block ${theme.ctaBg} text-white font-bold rounded-xl px-6 py-3 text-lg`}>
          Risparmio stimato: {savings.toLocaleString("it-IT")}€/anno
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="calculator-lead-form">
      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-2">
        {[0, 1, 2, 3, 4].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-all ${step >= s ? theme.ctaBg : "bg-slate-200"}`} />
        ))}
      </div>

      {/* Step 0: Property Type */}
      {step === 0 && (
        <div className="space-y-3" data-testid="calc-step-property">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Che tipo di abitazione hai?</h3>
          <div className="grid grid-cols-2 gap-2.5">
            {PROPERTY_TYPES.map((pt) => {
              const Icon = pt.icon;
              const sel = calc.property_type === pt.id;
              return (
                <button key={pt.id} onClick={() => { setCalc(p => ({ ...p, property_type: pt.id })); setStep(1); }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${sel ? `${theme.ctaBg} text-white border-transparent` : "border-slate-200 hover:border-slate-300 text-slate-700"}`}
                  data-testid={`calc-property-${pt.id}`}>
                  <Icon className="w-7 h-7" />
                  <span className="text-sm font-semibold">{pt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 1: Monthly Bill */}
      {step === 1 && (
        <div className="space-y-3" data-testid="calc-step-bill">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Quanto paghi di bolletta elettrica?</h3>
          <div className="space-y-2">
            {BILL_RANGES.map((b) => (
              <button key={b.id} onClick={() => { setCalc(p => ({ ...p, monthly_bill: b.label, bill_value: b.value })); setStep(2); }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all hover:scale-[1.01] ${calc.monthly_bill === b.label ? `${theme.ctaBg} text-white border-transparent` : "border-slate-200 hover:border-slate-300"}`}
                data-testid={`calc-bill-${b.id}`}>
                <span className="text-sm font-semibold">{b.label}</span>
              </button>
            ))}
          </div>
          <button onClick={() => setStep(0)} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Indietro
          </button>
        </div>
      )}

      {/* Step 2: Region */}
      {step === 2 && (
        <div className="space-y-3" data-testid="calc-step-region">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>In quale regione si trova l'immobile?</h3>
          <select value={calc.region} onChange={(e) => { const r = e.target.value; setCalc(p => ({ ...p, region: r })); if (r) goToResult(r); }}
            className="flex h-11 w-full rounded-xl border border-input bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="calc-region-select">
            <option value="">Seleziona la regione...</option>
            {Object.keys(regioniProvince).sort().map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={() => setStep(1)} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Indietro
          </button>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && (
        <div className="space-y-4 text-center" data-testid="calc-step-result">
          <div className="py-4">
            <Sun className="w-10 h-10 text-amber-500 mx-auto mb-2 animate-pulse" />
            <p className="text-sm text-slate-500 mb-1">Il tuo risparmio annuo stimato con il fotovoltaico</p>
            <div className={`text-4xl sm:text-5xl font-black ${theme.textAccent || "text-emerald-600"}`} style={{ fontFamily: "Outfit, sans-serif" }} data-testid="calc-savings-amount">
              {savings.toLocaleString("it-IT")}€
              <span className="text-lg font-medium text-slate-400">/anno</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-slate-50 rounded-xl p-2.5">
              <p className="text-[10px] text-slate-400">Abitazione</p>
              <p className="text-xs font-bold text-slate-700 capitalize">{calc.property_type}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-2.5">
              <p className="text-[10px] text-slate-400">Bolletta</p>
              <p className="text-xs font-bold text-slate-700">{calc.monthly_bill}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-2.5">
              <p className="text-[10px] text-slate-400">Regione</p>
              <p className="text-xs font-bold text-slate-700">{calc.region}</p>
            </div>
          </div>
          <button onClick={() => setStep(4)}
            className={`w-full ${theme.ctaBg} text-white font-bold rounded-xl py-3.5 text-sm shadow-xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity`}
            data-testid="calc-continue-to-contact">
            <Zap className="w-4 h-4" /> {landing.cta_result_text || "Ricevi un Preventivo Gratuito"} <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => setStep(2)} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 mx-auto">
            <ArrowLeft className="w-3 h-3" /> Ricalcola
          </button>
        </div>
      )}

      {/* Step 4: Contact Details */}
      {step === 4 && (
        <div className="space-y-3" data-testid="calc-step-contact">
          <div className={`text-center py-2 ${theme.ctaBg} text-white rounded-xl text-sm font-bold`}>
            Risparmio stimato: {savings.toLocaleString("it-IT")}€/anno
          </div>
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Inserisci i tuoi dati per il preventivo</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Nome *" className="h-11 rounded-xl" data-testid="calc-form-name" />
            <Input value={form.surname} onChange={(e) => setForm(p => ({ ...p, surname: e.target.value }))} placeholder="Cognome *" className="h-11 rounded-xl" data-testid="calc-form-surname" />
          </div>
          <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email *" className="h-11 rounded-xl" data-testid="calc-form-email" />
          <div>
            <Input value={form.phone} onChange={(e) => handlePhoneChange(e.target.value)} placeholder="Telefono *" className={`h-11 rounded-xl ${phoneError ? "border-red-400" : ""}`} data-testid="calc-form-phone" disabled={phoneVerified} />
            {phoneError && <p className="text-[10px] text-red-500 mt-0.5">{phoneError}</p>}
          </div>

          {/* OTP Section */}
          {otpRequired && (
            <div className="bg-slate-50 rounded-xl p-3 space-y-2">
              {!phoneVerified ? (
                <>
                  {!otpSent ? (
                    <button onClick={sendOtp} disabled={verifying || !form.phone || !!phoneError}
                      className="w-full bg-slate-900 text-white font-semibold rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="calc-otp-send">
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                      Invia Codice OTP
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-600 text-center font-medium">Inserisci il codice a 6 cifre</p>
                      <div className="flex justify-center">
                        <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                          <InputOTPGroup>
                            {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <button onClick={verifyOtp} disabled={verifying || otpCode.length < 6}
                        className="w-full bg-emerald-500 text-white font-semibold rounded-xl py-2 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="calc-otp-verify">
                        {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Verifica Codice
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1 justify-center">
                  <CheckCircle2 className="w-4 h-4" /> Numero verificato
                </p>
              )}
            </div>
          )}

          <select value={form.province} onChange={(e) => setForm(p => ({ ...p, province: e.target.value }))}
            className="flex h-11 w-full rounded-xl border border-input bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="calc-form-province">
            <option value="">Seleziona Provincia ({calc.region}) *</option>
            {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>

          {/* Privacy */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.privacy_accepted} onChange={(e) => setForm(p => ({ ...p, privacy_accepted: e.target.checked }))}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 shrink-0" data-testid="calc-form-privacy" />
            <CheckboxLabel label={privacyText || "Acconsento al trattamento dei miei dati personali."} linkText={privacyLink.text} linkUrl={privacyLink.url} required />
          </label>
          {extraCheckboxes.map((cb, idx) => (
            <label key={idx} className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={!!extraChecked[idx]} onChange={(e) => setExtraChecked(prev => ({ ...prev, [idx]: e.target.checked }))}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 shrink-0" data-testid={`calc-form-extra-cb-${idx}`} />
              <CheckboxLabel label={cb.label} linkText={cb.link_text} linkUrl={cb.link_url} required={cb.required} />
            </label>
          ))}

          <button onClick={submit} disabled={loading || !canSubmitContact}
            className={`w-full ${theme.ctaBg} text-white font-bold rounded-xl py-3.5 text-sm shadow-xl flex items-center justify-center gap-2 disabled:opacity-50`}
            data-testid="calc-form-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {landing.cta_text || "Richiedi Preventivo Gratuito"}
          </button>
          <button onClick={() => setStep(3)} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 mx-auto">
            <ArrowLeft className="w-3 h-3" /> Indietro
          </button>
        </div>
      )}
    </div>
  );
}
